import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Plus, Edit2, Trash2, Eye, Search, Users, IndianRupee, Building2, Phone, Mail,
  MapPin, TrendingUp, CheckCircle, Clock, ArrowLeft, FileText, CreditCard, ChevronRight
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

function formatCurrency(v) {
  if (!v || v === 0) return '₹0';
  return `₹${Number(v).toLocaleString('en-IN')}`;
}

function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; }
}

export default function Agents({ user, onLogout }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [detailAgent, setDetailAgent] = useState(null);
  const [agentStats, setAgentStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [form, setForm] = useState({ name: '', mobile: '', email: '', address: '', area: '' });

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/agents'); setAgents(res.data || []); }
    catch { toast.error('Agents load nahi ho paye'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const resetForm = () => setForm({ name: '', mobile: '', email: '', address: '', area: '' });

  const openAdd = () => { resetForm(); setEditingAgent(null); setDialogOpen(true); };
  const openEdit = (agent) => {
    setEditingAgent(agent);
    setForm({ name: agent.name || '', mobile: agent.mobile || '', email: agent.email || '', address: agent.address || '', area: agent.area || '' });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingAgent) {
        await api.put(`/agents/${editingAgent.id}`, form);
        toast.success('Agent updated!');
      } else {
        await api.post('/agents', form);
        toast.success('Agent added!');
      }
      setDialogOpen(false);
      fetchAgents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kya aap is agent ko delete karna chahte hain?')) return;
    try { await api.delete(`/agents/${id}`); toast.success('Agent deleted'); fetchAgents(); }
    catch { toast.error('Delete failed'); }
  };

  const viewDetail = async (agent) => {
    setDetailAgent(agent);
    setLoadingStats(true);
    try {
      const res = await api.get(`/agents/${agent.id}/stats`);
      setAgentStats(res.data);
    } catch { toast.error('Stats load nahi ho paye'); setAgentStats(null); }
    finally { setLoadingStats(false); }
  };

  const backToList = () => { setDetailAgent(null); setAgentStats(null); };

  const filtered = searchQuery.trim()
    ? agents.filter(a => (a.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (a.mobile || '').includes(searchQuery) || (a.area || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : agents;

  // Monthly chart data
  const monthlyChart = agentStats?.monthly
    ? Object.entries(agentStats.monthly).sort(([a], [b]) => a.localeCompare(b)).map(([m, d]) => ({
        month: new Date(m + '-01').toLocaleDateString('en-US', { month: 'short' }),
        bookings: d.bookings,
        revenue: d.revenue
      }))
    : [];

  // ===== AGENT DETAIL VIEW =====
  if (detailAgent) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="space-y-4 max-w-6xl mx-auto" data-testid="agent-detail-page">
          <Button variant="ghost" onClick={backToList} className="gap-1 text-slate-500 hover:text-slate-700 mb-1" data-testid="back-to-agents">
            <ArrowLeft size={16} /> Back to Agents
          </Button>

          {/* Agent Header Card */}
          <div className="relative overflow-hidden rounded-xl" style={{ background: 'linear-gradient(135deg, #0B1120, #1a2840)' }}>
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-5" style={{ background: '#C9A84C' }}></div>
            <div className="p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold" style={{ background: '#C9A84C20', color: '#C9A84C' }}>
                  {(detailAgent.name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{detailAgent.name}</h2>
                  <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                    {detailAgent.mobile && <span className="flex items-center gap-1"><Phone size={12} /> {detailAgent.mobile}</span>}
                    {detailAgent.email && <span className="flex items-center gap-1"><Mail size={12} /> {detailAgent.email}</span>}
                    {detailAgent.area && <span className="flex items-center gap-1"><MapPin size={12} /> {detailAgent.area}</span>}
                  </div>
                </div>
              </div>
              <span className={`px-3 py-1 text-xs font-bold rounded-full ${detailAgent.status === 'active' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                {detailAgent.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {loadingStats ? (
            <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
              <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div> Loading stats...
            </div>
          ) : agentStats ? (
            <>
              {/* Performance Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {[
                  { label: 'Total Bookings', value: agentStats.total_bookings, icon: FileText, color: '#2563eb', bg: '#eff6ff' },
                  { label: 'Total Revenue', value: formatCurrency(agentStats.total_revenue), icon: IndianRupee, color: '#059669', bg: '#f0fdf4' },
                  { label: 'Collected', value: formatCurrency(agentStats.total_collected), icon: CheckCircle, color: '#059669', bg: '#f0fdf4' },
                  { label: 'Pending', value: formatCurrency(agentStats.total_pending), icon: Clock, color: '#d97706', bg: '#fffbeb' },
                  { label: 'Fully Paid', value: agentStats.fully_paid, icon: CreditCard, color: '#7c3aed', bg: '#f5f3ff' },
                ].map((s, i) => (
                  <div key={i} className="rounded-xl border p-4" style={{ background: s.bg }} data-testid={`agent-stat-${i}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <s.icon size={14} style={{ color: s.color }} />
                      <span className="text-xs text-slate-500 font-medium">{s.label}</span>
                    </div>
                    <p className="text-xl font-bold" style={{ color: '#0B1120' }}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Monthly Chart + Bookings List */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Monthly Performance Chart */}
                {monthlyChart.length > 0 && (
                  <Card className="lg:col-span-1 border-0 shadow-md">
                    <CardHeader className="pb-1">
                      <CardTitle className="text-sm flex items-center gap-2 text-slate-700"><TrendingUp size={14} className="text-emerald-600" /> Monthly Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={monthlyChart}>
                            <defs>
                              <linearGradient id="agentGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#0B1120" stopOpacity={0.15} />
                                <stop offset="100%" stopColor="#0B1120" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="month" fontSize={10} stroke="#94a3b8" />
                            <YAxis fontSize={10} stroke="#94a3b8" tickFormatter={v => `₹${(v/100000).toFixed(0)}L`} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={v => [`₹${Number(v).toLocaleString('en-IN')}`, 'Revenue']} />
                            <Area type="monotone" dataKey="revenue" stroke="#0B1120" strokeWidth={2} fill="url(#agentGrad)" dot={{ r: 3, fill: '#C9A84C' }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Bookings List */}
                <Card className={`${monthlyChart.length > 0 ? 'lg:col-span-2' : 'lg:col-span-3'} border-0 shadow-md`}>
                  <CardHeader className="pb-2 border-b bg-slate-50/50">
                    <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
                      <Building2 size={14} className="text-blue-500" /> All Bookings ({agentStats.bookings.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {agentStats.bookings.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 text-sm">Koi booking nahi hai is agent ki</div>
                    ) : (
                      <div className="max-h-80 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Customer</TableHead>
                              <TableHead>Property</TableHead>
                              <TableHead>Amount</TableHead>
                              <TableHead>Paid</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {agentStats.bookings.map((b, i) => (
                              <TableRow key={b.id || i} className="hover:bg-slate-50" data-testid={`agent-booking-${i}`}>
                                <TableCell>
                                  <p className="font-semibold text-sm">{b.customer_name}</p>
                                  <p className="text-xs text-slate-400">{b.mobile_number}</p>
                                </TableCell>
                                <TableCell className="text-sm">{b.property_name}<br /><span className="text-xs text-slate-400">{b.property_size}</span></TableCell>
                                <TableCell className="font-semibold text-sm">{formatCurrency(b.total_price)}</TableCell>
                                <TableCell className="text-sm text-emerald-600 font-medium">{formatCurrency(b.total_paid)}</TableCell>
                                <TableCell>
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${b.payment_status === 'Fully Paid' ? 'bg-emerald-100 text-emerald-700' : b.payment_status === 'Partially Paid' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                    {b.payment_status}
                                  </span>
                                </TableCell>
                                <TableCell className="text-xs text-slate-500">{formatDate(b.created_at)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : null}
        </div>
      </Layout>
    );
  }

  // ===== AGENT LIST VIEW =====
  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4 max-w-6xl mx-auto" data-testid="agents-page">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="flex items-center gap-2">
                <Users size={20} className="text-blue-600" />
                Sales Agents
              </CardTitle>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input placeholder="Search agent..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 w-56" data-testid="agent-search" />
                </div>
                <Button onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="add-agent-btn">
                  <Plus size={16} /> Add Agent
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8 text-slate-500 gap-2">
                <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div> Loading...
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Users size={40} className="mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Koi agent nahi mila</p>
                <p className="text-sm mt-1">Naya agent add karne ke liye "Add Agent" button click karein</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map(agent => (
                  <div key={agent.id} className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all hover:border-slate-300 group cursor-pointer" onClick={() => viewDetail(agent)} data-testid={`agent-card-${agent.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold" style={{ background: '#0B112010', color: '#0B1120' }}>
                          {(agent.name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{agent.name}</p>
                          {agent.area && <p className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={10} /> {agent.area}</p>}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${agent.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {agent.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm text-slate-600">
                      {agent.mobile && <p className="flex items-center gap-2"><Phone size={13} className="text-slate-400" /> {agent.mobile}</p>}
                      {agent.email && <p className="flex items-center gap-2"><Mail size={13} className="text-slate-400" /> {agent.email}</p>}
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                      <span className="text-xs text-blue-600 font-medium flex items-center gap-1 group-hover:underline"><Eye size={12} /> View Details <ChevronRight size={12} /></span>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(agent)} className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" data-testid={`edit-agent-${agent.id}`}><Edit2 size={13} /></Button>
                        {user?.role === 'admin' && <Button size="sm" variant="ghost" onClick={() => handleDelete(agent.id)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" data-testid={`delete-agent-${agent.id}`}><Trash2 size={13} /></Button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Agent Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>{editingAgent ? 'Edit Agent' : 'Add New Agent'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSave} className="space-y-3">
              <div className="space-y-1"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="agent-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Mobile</Label><Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="agent-mobile" /></div>
                <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="agent-email" /></div>
              </div>
              <div className="space-y-1"><Label>Area / Location</Label><Input value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} placeholder="e.g. South Delhi, Noida" data-testid="agent-area" /></div>
              <div className="space-y-1"><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="agent-address" /></div>
              <Button type="submit" disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-agent-btn">
                {saving ? 'Saving...' : editingAgent ? 'Update Agent' : 'Add Agent'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
